<template>

    <div class="wizard-component">

        <!-- Landlord type -->

        <template v-if="usingBehalfOfLandlord && showLandlordType">
            <p class="subject">{{ $t('digital.tabs.landlord-type') }}</p>
            <div class="double-radio">
                <label for="landlordTypePrivate" class="custom-radio">
                    <input
                        name="landlord_type"
                        id="landlordTypePrivate"
                        type="radio"
                        value="private"
                        @change="commit()"
                        v-model="draftContract.definition.landlord_type"
                    />
                    <span class="radio-circle"></span>
                    <p>{{ $t('digital.tabs.landlord-type-private') }}</p>
                </label>
                <label for="landlordTypeCommercial" class="custom-radio">
                    <input
                        name="landlord_type"
                        id="landlordTypeCommercial"
                        type="radio"
                        value="commercial"
                        @change="commit()"
                        v-model="draftContract.definition.landlord_type"
                    />
                    <span class="radio-circle"></span>
                    <p>{{ $t('digital.tabs.landlord-type-commercial') }}</p>
                </label>
            </div>
        </template>

        <!-- CVR -->

        <div v-if="showCvr" class="inputs data-input">
            <div class="input-container double">
                <p class="subject">{{ $t('landlord-info.input.cvr') }}</p>
                <div class="input-inner">
                    <validation-provider name="CVR" rules="numeric|length:8" v-slot="{failed}" slim>
                        <input
                            type="text"
                            maxlength="8"
                            :class="{'has-error': failed}"
                            @change="commit()"
                            v-on:keyup.enter="lookup()"
                            v-model="draftContract.definition.landlord_vatno"
                        />
                    </validation-provider>
                </div>
            </div>
            <div class="input-container double">
                <p class="subject">&nbsp;</p>
                <div class="input-inner">
                    <button
                        style="margin-top: 0px;"
                        class="purple"
                        v-on:click="lookup()"
                        :disabled="saving || String(draftContract.definition.landlord_vatno).length != 8"
                    >
                        {{ $t('landlord.lookup-cvrapi.button') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Landlord Details -->

        <div
            v-if="!usingBehalfOfLandlord || (draftContract.definition.landlord_type === 'commercial' || (draftContract.definition.landlord_type === 'private' && showPrivateFields))"
            class="inputs data-input" style="clear: both;">

            <div class="input-container">
                <p class="subject">{{ $t('user.settings.label.name') }}</p>
                <div class="input-inner">
                    <validation-provider rules="required" v-slot="{failed}" immediate slim>
                        <input
                            type="text"
                            :class="{'has-error': failed}"
                            @change="commit()"
                            v-model="draftContract.definition.landlord_name"
                        />
                    </validation-provider>
                </div>
            </div>

            <div class="input-container">
                <p class="subject">{{ $t('input.placeholder.address') }}</p>
                <div class="input-inner">
                    <validation-provider rules="required" v-slot="{failed}" immediate slim>
                        <input
                            type="text"
                            :class="{'has-error': failed}"
                            @change="commit()"
                            v-model="draftContract.definition.landlord_address"
                        />
                    </validation-provider>
                </div>
            </div>

            <div class="input-container double">
                <p class="subject">{{ $t('input.placeholder.zipcode') }}</p>
                <div class="input-inner">
                    <validation-provider rules="required|numeric" v-slot="{failed}" immediate slim>
                        <input
                            type="text"
                            :class="{'has-error': failed}"
                            @change="commit()"
                            v-model="draftContract.definition.landlord_zipcode"
                        />
                    </validation-provider>
                </div>
            </div>

            <div class="input-container double">
                <p class="subject">{{ $t('input.placeholder.city') }}</p>
                <div class="input-inner">
                    <validation-provider rules="required" v-slot="{failed}" immediate slim>
                        <input
                            type="text"
                            :class="{'has-error': failed}"
                            @change="commit()"
                            v-model="draftContract.definition.landlord_city"
                        />
                    </validation-provider>
                </div>
            </div>
        </div>

        <div class="inputs data-input">
            <div class="input-container">
                <p class="subject">{{ $t('input.placeholder.email') }}</p>
                <div class="input-inner">
                    <validation-provider rules="required|email" v-slot="{failed}" immediate slim>
                        <input
                            type="text"
                            :class="{'has-error': failed}"
                            @change="commit()"
                            v-model="draftContract.definition.landlord_email"
                            :disabled="usingBehalfOfLandlord === false || showPrivateFields"
                        />
                    </validation-provider>
                </div>
            </div>
        </div>

        <!-- Bank Details for Private Landlord (on approving step) -->

        <div v-if="showBankFormFields" class="wizard-component">
            <h2>{{ $t('digital.tabs.payments.bankdetails') }}</h2>

            <div class="inputs data-input">
                <div class="input-container double">
                    <p class="subject">{{ $t('bankRegNo') }}</p>
                    <div class="input-inner">
                        <validation-provider rules="required|numeric" v-slot="{failed}" immediate slim>
                            <input
                                type="text"
                                v-model="bankRegNo"
                            />
                        </validation-provider>
                    </div>
                </div>
                <div class="input-container double">
                    <p class="subject">{{ $t('bankAccountNo') }}</p>
                    <div class="input-inner">
                        <validation-provider rules="required" v-slot="{failed}" immediate slim>
                            <input
                                type="text"
                                :class="{'has-error': failed}"
                                v-model="bankAccountNo"
                            />
                        </validation-provider>
                    </div>
                </div>
                <div class="input-container">
                    <p class="subject">{{ $t('bankName') }}</p>
                    <div class="input-inner">
                        <form>
                            <validation-provider rules="required" v-slot="{failed}" immediate slim>
                                <input
                                    type="text"
                                    :class="{'has-error': failed}"
                                    v-model="bankName"
                                    autofill="off"
                                    autocomplete="false"
                                />
                            </validation-provider>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <div class="inputs data-input" v-if="showUpdateButton">
            <button class="button purple"
                    :disabled="updating"
                    v-on:click="updateInfo()"
            >
                <template v-if="!updating">{{ $t('save') }}</template>
                <template v-if="updating">{{ $t('saving') }} ...</template>
            </button>
        </div>

    </div>

</template>


<script>

import {mapGetters} from 'vuex';
import {store} from '../../../store/index'

export default {
    props: [
        'usingBehalfOfLandlord',
        'showUpdateButton',
        'showBankFormFields',
        'showLandlordType',
        'showPrivateFields',
        'showCvr',
        'defaultEmail',
        'draftContract',
        'cvr'
    ],
    data: () => ({
        updating: false,
        saving: false,
        bankRegNo: '',
        bankAccountNo: '',
        bankName: '',
    }),
    methods: {
        lookup() {
            this.saving = true;
            axios({
                method: 'GET',
                url: 'https://cvrapi.dk/api?search=' + this.draftContract.definition.landlord_vatno + '&country=dk',
                cancelToken: window.AuthorizationCancelSource.token,
            }).then(response => {
                this.draftContract.definition.landlord_name = response.data.name;
                this.draftContract.definition.landlord_address = response.data.address;
                this.draftContract.definition.landlord_zipcode = response.data.zipcode;
                this.draftContract.definition.landlord_city = response.data.city;

                if (this.usingBehalfOfLandlord && response.data.email !== null) {
                    this.draftContract.definition.landlord_email = response.data.email
                }

                this.commit();

            }).finally(e => {
                this.saving = false;
            });
        },
        commit() {
            store.commit('setLandlordData', {
                name: this.draftContract.definition.landlord_name,
                address: this.draftContract.definition.landlord_address,
                zipCode: this.draftContract.definition.landlord_zipcode,
                city: this.draftContract.definition.landlord_city,
                email: this.draftContract.definition.landlord_email,
                cvr: this.draftContract.definition.landlord_vatno,
                type: this.draftContract.definition.landlord_type
            });
        },
        /* Approving step */
        updateInfo() {
            this.updating = true;
            axios({
                method: 'PATCH',
                url: window.config.apiUrl + 'api/draft-contract/' + this.$route.params.id + '/update-landlord',
                cancelToken: window.AuthorizationCancelSource.token,
                data: {
                    name: this.draftContract.definition.landlord_name,
                    address: this.draftContract.definition.landlord_address,
                    zipcode: this.draftContract.definition.landlord_zipcode,
                    city: this.draftContract.definition.landlord_city,
                    updateBank: this.showBankFormFields,
                    bankRegNo: this.bankRegNo,
                    bankAccountNo: this.bankAccountNo,
                    bankName: this.bankName
                }
            }).then(response => {
                this.$emit('save');
            }).finally((e) => {
                this.updating = false;
            });
        },
        loadVat() {
            axios({
                method: 'GET',
                url: window.config.apiUrl + 'api/user/suggest-vat',
                cancelToken: window.AuthorizationCancelSource.token,
            }).then(response => {
                this.draftContract.definition.landlord_vatno = response.data.vat_no;
            });
        },
    },
    computed: {
        ...mapGetters([
            'getLandlordData',
            'getToken',
        ])
    },
    mounted() {
        if (!this.draftContract.definition.landlord_vatno) {
            this.loadVat();
        }

        if (this.usingBehalfOfLandlord === false) {
            this.draftContract.definition.landlord_email = this.defaultEmail;
        }
    },
    watch: {
        usingBehalfOfLandlord: function (value) {
            if (value === false) {
                this.draftContract.definition.landlord_email = this.defaultEmail;
            }
        }
    }

}
</script>


<style scoped>

input:disabled {
    background-color: #efefef !important;
}

</style>
